import { useState } from 'react';

import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import { Icon } from '~/modules/elements/akui/icon';
import RemoteMicClient, { serverRpc } from '~/modules/remote-mic/network/client';
import { useServerMutation } from '~/modules/remote-mic/network/client/hooks/use-server-mutation';
import { MIC_ID_KEY } from '~/modules/remote-mic/network/client/network-client';
import storage from '~/modules/utils/storage';
import { InfoButton, InfoLine } from '~/routes/remote-mic/components/info-reveal';
import NumericInput from '~/routes/remote-mic/components/numeric-input';
import { RemoteMicrophoneLagSetting, useSettingValue } from '~/routes/settings/settings-state';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MicrophoneSettingsSheet({ open, onClose }: Props) {
  const [confirmingReset, setConfirmingReset] = useState(false);
  // The client is the source of truth: the value is read from local storage and re-sent on every
  // connection, so it survives a refresh where a server-held one would not.
  const [currentValue, setCurrentValue] = useSettingValue(RemoteMicrophoneLagSetting);
  const { mutate, loading: mutating } = useServerMutation(async (newValue: number) => {
    await serverRpc.settings.setMicrophoneLag(newValue);
  });

  const handleChange = (value: number) => {
    setCurrentValue(value);
    void mutate(value);
  };

  const reset = () => {
    RemoteMicClient.disconnect();
    storage.local.removeItem('remote_mic_name');
    storage.local.removeItem(MIC_ID_KEY);
    global.location?.reload();
  };

  return (
    <>
      <BottomSheet open={open && !confirmingReset} onClose={onClose} title="Microphone settings">
        <div className="flex flex-col gap-3">
          <span className="flex items-center gap-2 text-[0.8125rem] tracking-[0.08em] text-white/50 uppercase">
            Microphone lag
            <InfoButton infoKey="MicLag" />
          </span>
          <NumericInput
            value={currentValue}
            onChange={handleChange}
            unit="ms"
            disabled={mutating}
            step={25}
            surface="sheet"
            data-test="microphone-delay"
          />
          <InfoLine infoKey="MicLag">Shifts when the game hears you. Raise it if your notes register late.</InfoLine>
          <button
            type="button"
            onClick={() => setConfirmingReset(true)}
            data-test="reset-microphone"
            className="flex h-16 cursor-pointer items-center gap-3 rounded-[1.125rem] border-none bg-white/8 px-4.5 text-left text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
            <Icon icon="ic:baseline-restart-alt" size={6} className="text-active shrink-0" />
            <span className="min-w-0 flex-1 truncate text-[1.0625rem]">Reset microphone</span>
            <InfoButton infoKey="ResetMic" />
            <Icon icon="ic:baseline-arrow-forward" size={5.5} className="shrink-0 opacity-70" />
          </button>
          <InfoLine infoKey="ResetMic">Forgets this phone&#39;s name and ID, then rejoins from the start.</InfoLine>
        </div>
      </BottomSheet>

      <BottomSheet open={confirmingReset} onClose={() => setConfirmingReset(false)} title="Reset microphone?">
        <div className="flex flex-col gap-3.5">
          <span className="text-[1.0625rem] leading-[1.45] text-white/90">
            Forgets your name and mic ID, then rejoins as a new microphone.
          </span>
          <span className="text-[0.9375rem] leading-[1.45] text-white/65">Your scores stay on the game.</span>
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => setConfirmingReset(false)}
              className="flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-white/8 text-[1.0625rem] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
              Cancel
            </button>
            <button
              type="button"
              onClick={reset}
              data-test="reset-confirm"
              className="bg-active flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
              Reset
            </button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
